import { useState } from "react";
import { Link } from "react-router-dom";
import { Download, FileSpreadsheet } from "lucide-react";
import toast from "react-hot-toast";
import {
  useRevenueByService,
  useProductivity,
  useUpcomingTaxes,
  useUpcomingDeadlines,
  useLegalProceduresKpis,
  useCarteraSummary,
  downloadReportCsv,
  DEADLINE_TYPE_LABEL,
  DeadlineType,
} from "@/api/reports";
import { LEGAL_CASE_STATUS_LABEL } from "@/api/legal";
import { PROCEDURE_STATUS_LABEL } from "@/api/procedures";
import { useAuthStore } from "@/stores/authStore";
import { getApiErrorMessage } from "@/api/client";

const currency = new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 });

const TABS = [
  { key: "deadlines", label: "Alertas de vencimientos" },
  { key: "indicadores", label: "Indicadores Jurídico/Trámites" },
  { key: "revenue", label: "Ingresos por servicio" },
  { key: "productivity", label: "Productividad" },
  { key: "taxes", label: "Impuestos por vencer" },
  { key: "cartera", label: "Cartera" },
] as const;

type TabKey = (typeof TABS)[number]["key"];

export default function ReportsPage() {
  const [tab, setTab] = useState<TabKey>("deadlines");
  const hasPermission = useAuthStore((s) => s.hasPermission);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">Reportes</h1>
        <p className="text-sm text-gray-500">Vistas agregadas sobre los datos ya registrados: ingresos, productividad, impuestos y cartera.</p>
      </div>

      <div className="flex gap-1 border-b border-gray-200">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`px-3 py-2 text-sm font-medium ${
              tab === t.key ? "border-b-2 border-brand-600 text-brand-600" : "text-gray-500 hover:text-gray-700"
            }`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "deadlines" && <DeadlinesTab canExport={hasPermission("reports.export")} />}
      {tab === "indicadores" && <IndicadoresTab />}
      {tab === "revenue" && <RevenueByServiceTab canExport={hasPermission("reports.export")} />}
      {tab === "productivity" && <ProductivityTab canExport={hasPermission("reports.export")} />}
      {tab === "taxes" && <UpcomingTaxesTab canExport={hasPermission("reports.export")} />}
      {tab === "cartera" && <CarteraSummaryTab />}
    </div>
  );
}

const DEADLINE_TYPE_OPTIONS: Array<{ value: DeadlineType | "TODOS"; label: string }> = [
  { value: "TODOS", label: "Todos los tipos" },
  { value: "TRAMITE", label: DEADLINE_TYPE_LABEL.TRAMITE },
  { value: "IMPUESTO", label: DEADLINE_TYPE_LABEL.IMPUESTO },
  { value: "JURIDICO", label: DEADLINE_TYPE_LABEL.JURIDICO },
  { value: "FACTURA", label: DEADLINE_TYPE_LABEL.FACTURA },
  { value: "DOCUMENTO", label: DEADLINE_TYPE_LABEL.DOCUMENTO },
];

const DEADLINE_TYPE_BADGE: Record<DeadlineType, string> = {
  TRAMITE: "bg-blue-100 text-blue-700",
  IMPUESTO: "bg-purple-100 text-purple-700",
  JURIDICO: "bg-indigo-100 text-indigo-700",
  FACTURA: "bg-teal-100 text-teal-700",
  DOCUMENTO: "bg-gray-200 text-gray-700",
};

function DeadlinesTab({ canExport }: { canExport: boolean }) {
  const [daysAhead, setDaysAhead] = useState(30);
  const [typeFilter, setTypeFilter] = useState<DeadlineType | "TODOS">("TODOS");
  const { data, isLoading } = useUpcomingDeadlines(daysAhead);
  const rows = (data ?? []).filter((row) => typeFilter === "TODOS" || row.type === typeFilter);

  const handleExportCsv = async () => {
    try {
      await downloadReportCsv("/reports/upcoming-deadlines.csv", "alertas-de-vencimientos.csv", { daysAhead });
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const handleExportExcel = async () => {
    try {
      await downloadReportCsv("/reports/upcoming-deadlines.xlsx", "alertas-de-vencimientos.xlsx", { daysAhead });
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="card overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 p-4">
        <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <span>Ventana:</span>
            <select className="input-field w-auto" value={daysAhead} onChange={(e) => setDaysAhead(Number(e.target.value))}>
              <option value={15}>15 días</option>
              <option value={30}>30 días</option>
              <option value={60}>60 días</option>
              <option value={90}>90 días</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <span>Tipo:</span>
            <select className="input-field w-auto" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value as DeadlineType | "TODOS")}>
              {DEADLINE_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        {canExport && (
          <div className="flex gap-2">
            <button type="button" className="btn-secondary" onClick={handleExportExcel}>
              <FileSpreadsheet size={14} className="mr-1.5" /> Exportar Excel
            </button>
            <button type="button" className="btn-secondary" onClick={handleExportCsv}>
              <Download size={14} className="mr-1.5" /> Exportar CSV
            </button>
          </div>
        )}
      </div>
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left font-medium text-gray-500">Tipo</th>
            <th className="px-4 py-3 text-left font-medium text-gray-500">Cliente</th>
            <th className="px-4 py-3 text-left font-medium text-gray-500">Detalle</th>
            <th className="px-4 py-3 text-left font-medium text-gray-500">Vencimiento</th>
            <th className="px-4 py-3 text-right font-medium text-gray-500">Días</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {isLoading && (
            <tr>
              <td colSpan={5} className="px-4 py-6 text-center text-gray-400">Cargando...</td>
            </tr>
          )}
          {!isLoading && rows.length === 0 && (
            <tr>
              <td colSpan={5} className="px-4 py-10 text-center text-gray-400">No hay vencimientos próximos en esta ventana.</td>
            </tr>
          )}
          {rows.map((row) => (
            <tr key={`${row.type}-${row.id}`} className="hover:bg-gray-50">
              <td className="px-4 py-3">
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${DEADLINE_TYPE_BADGE[row.type]}`}>
                  {DEADLINE_TYPE_LABEL[row.type]}
                </span>
              </td>
              <td className="px-4 py-3 text-gray-900">
                <Link to={`/clients/${row.clientId}`} className="hover:text-brand-600">
                  {row.clientName}
                </Link>
              </td>
              <td className="px-4 py-3 text-gray-600">{row.label}</td>
              <td className="px-4 py-3 text-gray-600">{row.dueDate}</td>
              <td className="px-4 py-3 text-right">
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${row.daysUntilDue < 0 ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
                  {row.daysUntilDue < 0 ? `${Math.abs(row.daysUntilDue)} días vencido` : `${row.daysUntilDue} días`}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function IndicadoresTab() {
  const { data, isLoading } = useLegalProceduresKpis();

  if (isLoading || !data) {
    return <div className="card p-4 text-sm text-gray-400">Cargando...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="mb-2 text-sm font-semibold text-gray-700">Jurídico</h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <SummaryCard label="Casos totales" value={data.legal.total} isCount />
          <SummaryCard label="Audiencias próx. 7 días" value={data.legal.upcomingHearings7d} isCount tone={data.legal.upcomingHearings7d > 0 ? "red" : "gray"} />
          <SummaryCard label="Monto en reclamación (activos)" value={data.legal.claimAmountActive} tone="brand" />
        </div>
        <div className="mt-3 card overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left font-medium text-gray-500">Estado</th>
                <th className="px-4 py-2 text-right font-medium text-gray-500">Casos</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {Object.entries(data.legal.byStatus).map(([status, count]) => (
                <tr key={status}>
                  <td className="px-4 py-2 text-gray-700">{LEGAL_CASE_STATUS_LABEL[status as keyof typeof LEGAL_CASE_STATUS_LABEL]}</td>
                  <td className="px-4 py-2 text-right text-gray-900">{count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <h2 className="mb-2 text-sm font-semibold text-gray-700">Trámites</h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <SummaryCard label="Trámites totales" value={data.procedures.total} isCount />
          <SummaryCard label="Vencidos" value={data.procedures.overdue} isCount tone={data.procedures.overdue > 0 ? "red" : "gray"} />
          <SummaryCard label="Próx. a vencer (7 días)" value={data.procedures.dueSoon7d} isCount tone={data.procedures.dueSoon7d > 0 ? "red" : "gray"} />
          <SummaryCard label="Costo de trámites activos" value={data.procedures.costActive} tone="brand" />
        </div>
        <div className="mt-3 card overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left font-medium text-gray-500">Estado</th>
                <th className="px-4 py-2 text-right font-medium text-gray-500">Trámites</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {Object.entries(data.procedures.byStatus).map(([status, count]) => (
                <tr key={status}>
                  <td className="px-4 py-2 text-gray-700">{PROCEDURE_STATUS_LABEL[status as keyof typeof PROCEDURE_STATUS_LABEL]}</td>
                  <td className="px-4 py-2 text-right text-gray-900">{count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function RevenueByServiceTab({ canExport }: { canExport: boolean }) {
  const { data, isLoading } = useRevenueByService();
  const total = (data ?? []).reduce((sum, r) => sum + r.totalRevenue, 0);

  const handleExportCsv = async () => {
    try {
      await downloadReportCsv("/reports/revenue-by-service.csv", "ingresos-por-servicio.csv");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const handleExportExcel = async () => {
    try {
      await downloadReportCsv("/reports/revenue-by-service.xlsx", "ingresos-por-servicio.xlsx");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="card overflow-hidden">
      <div className="flex items-center justify-between border-b border-gray-100 p-4">
        <p className="text-sm text-gray-600">Total: <span className="font-semibold text-gray-900">{currency.format(total)}</span></p>
        {canExport && (
          <div className="flex gap-2">
            <button type="button" className="btn-secondary" onClick={handleExportExcel}>
              <FileSpreadsheet size={14} className="mr-1.5" /> Exportar Excel
            </button>
            <button type="button" className="btn-secondary" onClick={handleExportCsv}>
              <Download size={14} className="mr-1.5" /> Exportar CSV
            </button>
          </div>
        )}
      </div>
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left font-medium text-gray-500">Descripción del ítem</th>
            <th className="px-4 py-3 text-right font-medium text-gray-500">N.° de facturas</th>
            <th className="px-4 py-3 text-right font-medium text-gray-500">Ingresos</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {isLoading && (
            <tr>
              <td colSpan={3} className="px-4 py-6 text-center text-gray-400">Cargando...</td>
            </tr>
          )}
          {!isLoading && (!data || data.length === 0) && (
            <tr>
              <td colSpan={3} className="px-4 py-10 text-center text-gray-400">Aún no hay facturas emitidas.</td>
            </tr>
          )}
          {data?.map((row) => (
            <tr key={row.description} className="hover:bg-gray-50">
              <td className="px-4 py-3 text-gray-900">{row.description}</td>
              <td className="px-4 py-3 text-right text-gray-600">{row.invoiceCount}</td>
              <td className="px-4 py-3 text-right font-medium text-gray-900">{currency.format(row.totalRevenue)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ProductivityTab({ canExport }: { canExport: boolean }) {
  const { data, isLoading } = useProductivity();

  const handleExportExcel = async () => {
    try {
      await downloadReportCsv("/reports/productivity.xlsx", "productividad.xlsx");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="card overflow-hidden">
      {canExport && (
        <div className="flex justify-end border-b border-gray-100 p-4">
          <button type="button" className="btn-secondary" onClick={handleExportExcel}>
            <FileSpreadsheet size={14} className="mr-1.5" /> Exportar Excel
          </button>
        </div>
      )}
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left font-medium text-gray-500">Responsable</th>
            <th className="px-4 py-3 text-right font-medium text-gray-500">Asignados</th>
            <th className="px-4 py-3 text-right font-medium text-gray-500">Completados</th>
            <th className="px-4 py-3 text-right font-medium text-gray-500">% Completado</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {isLoading && (
            <tr>
              <td colSpan={4} className="px-4 py-6 text-center text-gray-400">Cargando...</td>
            </tr>
          )}
          {!isLoading && (!data || data.length === 0) && (
            <tr>
              <td colSpan={4} className="px-4 py-10 text-center text-gray-400">
                Aún no hay tareas, trámites, impuestos o casos jurídicos asignados a un responsable.
              </td>
            </tr>
          )}
          {data?.map((row) => (
            <tr key={row.userId} className="hover:bg-gray-50">
              <td className="px-4 py-3 font-medium text-gray-900">{row.userName}</td>
              <td className="px-4 py-3 text-right text-gray-600">{row.assigned}</td>
              <td className="px-4 py-3 text-right text-gray-600">{row.completed}</td>
              <td className="px-4 py-3 text-right">
                <span
                  className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                    row.completionRate >= 75 ? "bg-green-100 text-green-700" : row.completionRate >= 40 ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {row.completionRate}%
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function UpcomingTaxesTab({ canExport }: { canExport: boolean }) {
  const [daysAhead, setDaysAhead] = useState(30);
  const { data, isLoading } = useUpcomingTaxes(daysAhead);

  const handleExportCsv = async () => {
    try {
      await downloadReportCsv("/reports/upcoming-taxes.csv", "impuestos-proximos-a-vencer.csv", { daysAhead });
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const handleExportExcel = async () => {
    try {
      await downloadReportCsv("/reports/upcoming-taxes.xlsx", "impuestos-proximos-a-vencer.xlsx", { daysAhead });
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="card overflow-hidden">
      <div className="flex items-center justify-between border-b border-gray-100 p-4">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span>Ventana:</span>
          <select className="input-field w-auto" value={daysAhead} onChange={(e) => setDaysAhead(Number(e.target.value))}>
            <option value={15}>15 días</option>
            <option value={30}>30 días</option>
            <option value={60}>60 días</option>
            <option value={90}>90 días</option>
          </select>
        </div>
        {canExport && (
          <div className="flex gap-2">
            <button type="button" className="btn-secondary" onClick={handleExportExcel}>
              <FileSpreadsheet size={14} className="mr-1.5" /> Exportar Excel
            </button>
            <button type="button" className="btn-secondary" onClick={handleExportCsv}>
              <Download size={14} className="mr-1.5" /> Exportar CSV
            </button>
          </div>
        )}
      </div>
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left font-medium text-gray-500">Cliente</th>
            <th className="px-4 py-3 text-left font-medium text-gray-500">Impuesto</th>
            <th className="px-4 py-3 text-left font-medium text-gray-500">Periodo</th>
            <th className="px-4 py-3 text-left font-medium text-gray-500">Vencimiento</th>
            <th className="px-4 py-3 text-right font-medium text-gray-500">Días</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {isLoading && (
            <tr>
              <td colSpan={5} className="px-4 py-6 text-center text-gray-400">Cargando...</td>
            </tr>
          )}
          {!isLoading && (!data || data.length === 0) && (
            <tr>
              <td colSpan={5} className="px-4 py-10 text-center text-gray-400">No hay obligaciones pendientes en esta ventana.</td>
            </tr>
          )}
          {data?.map((row) => (
            <tr key={row.id} className="hover:bg-gray-50">
              <td className="px-4 py-3 text-gray-900">
                <Link to={`/clients/${row.clientId}`} className="hover:text-brand-600">
                  {row.clientName}
                </Link>
              </td>
              <td className="px-4 py-3 text-gray-600">{row.taxType}</td>
              <td className="px-4 py-3 text-gray-600">{row.periodLabel}</td>
              <td className="px-4 py-3 text-gray-600">{row.dueDate}</td>
              <td className="px-4 py-3 text-right">
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${row.daysUntilDue < 0 ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
                  {row.daysUntilDue < 0 ? `${Math.abs(row.daysUntilDue)} días vencido` : `${row.daysUntilDue} días`}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CarteraSummaryTab() {
  const { data, isLoading } = useCarteraSummary();

  return (
    <div className="space-y-3">
      {data && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <SummaryCard label="Al día" value={data.totals.current} />
          <SummaryCard label="1-30 días" value={data.totals.days1To30} />
          <SummaryCard label="31-60 días" value={data.totals.days31To60} />
          <SummaryCard label="61-90 días" value={data.totals.days61To90} tone="red" />
          <SummaryCard label="90+ días" value={data.totals.daysOver90} tone="red" />
          <SummaryCard label="Total cartera" value={data.totals.totalOutstanding} tone="brand" />
        </div>
      )}
      <div className="card p-4 text-sm text-gray-500">
        {isLoading && "Cargando..."}
        {!isLoading && data && data.rows.length === 0 && "No hay saldos pendientes. Toda la cartera está al día."}
        {!isLoading && data && data.rows.length > 0 && (
          <>
            Ver el detalle completo por cliente en{" "}
            <Link to="/cartera" className="text-brand-600 hover:text-brand-800">
              la sección de Cartera
            </Link>
            .
          </>
        )}
      </div>
    </div>
  );
}

function SummaryCard({
  label,
  value,
  tone = "gray",
  isCount = false,
}: {
  label: string;
  value: number;
  tone?: "gray" | "red" | "brand";
  isCount?: boolean;
}) {
  const toneStyle: Record<string, string> = { gray: "text-gray-900", red: "text-red-600", brand: "text-brand-600" };
  return (
    <div className="card p-4">
      <p className="text-xs text-gray-400">{label}</p>
      <p className={`mt-1 text-lg font-semibold ${toneStyle[tone]}`}>{isCount ? value : currency.format(value)}</p>
    </div>
  );
}
